import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MapPin, Clock, BookmarkSimple, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ListingCard({ listing, index = 0 }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [boards, setBoards] = useState([]);
  const [newName, setNewName] = useState("");

  const openSave = async () => {
    if (!user) {
      toast("Sign in to save trips", { description: "Create an account to build trip boards." });
      navigate("/login");
      return;
    }
    try {
      const { data } = await api.get("/boards");
      setBoards(data);
      setOpen(true);
    } catch {
      toast.error("Could not load your boards");
    }
  };

  const saveTo = async (boardId) => {
    try {
      await api.post(`/boards/${boardId}/listings`, { listing_id: listing.id });
      toast.success("Saved to board");
      setOpen(false);
    } catch {
      toast.error("Failed to save");
    }
  };

  const createAndSave = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/boards", { name: newName });
      await saveTo(data.id);
      setNewName("");
    } catch {
      toast.error("Failed to create board");
    }
  };

  return (
    <article
      className="group relative overflow-hidden rounded-2xl border border-[#D2D5CD] bg-white transition-colors duration-200 hover:border-[#2E523A]/40 animate-fade-up"
      style={{ animationDelay: `${index * 70}ms` }}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="relative h-60 overflow-hidden">
        <img
          src={listing.image}
          alt={listing.title}
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
        />
        <span className="absolute top-4 left-4 bg-[#FAFAF7]/90 backdrop-blur text-[#1A1C19] text-xs font-bold px-3 py-1 rounded-full">
          {listing.category}
        </span>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              onClick={openSave}
              className="absolute top-3.5 right-4 h-10 w-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-[#E8B291] transition-colors duration-200"
              data-testid={`save-listing-${listing.id}`}
              aria-label="Save trip"
            >
              <BookmarkSimple size={20} weight="duotone" className="text-[#2E523A]" />
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Save to a trip board</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {boards.length === 0 && (
                <p className="text-sm text-muted-foreground py-2">No boards yet — create your first below.</p>
              )}
              {boards.map((b) => (
                <button
                  key={b.id}
                  onClick={() => saveTo(b.id)}
                  className="w-full text-left px-4 py-3 rounded-xl border border-[#D2D5CD] hover:border-[#2E523A] hover:bg-[#EAEBE6] transition-colors duration-200 font-medium"
                  data-testid={`board-option-${b.id}`}
                >
                  {b.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Input
                placeholder="New board name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="new-board-name-input"
              />
              <Button onClick={createAndSave} className="rounded-full bg-[#2E523A] hover:bg-[#26442f] shrink-0" data-testid="create-board-and-save">
                <Plus size={18} /> Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="p-6">
        <h3 className="font-display text-xl font-bold text-[#1A1C19] mb-1">{listing.title}</h3>
        <div className="flex items-center gap-4 text-sm text-[#63675E] mb-3">
          <span className="flex items-center gap-1"><MapPin size={15} /> {listing.location}</span>
          <span className="flex items-center gap-1"><Clock size={15} /> {listing.duration}</span>
        </div>
        <p className="text-sm text-[#63675E] leading-relaxed mb-4 line-clamp-2">{listing.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#63675E]">
            from <span className="font-display text-2xl font-bold text-[#2E523A]">${listing.price_from}</span>
          </span>
        </div>
      </div>
    </article>
  );
}
